import { applyDelta, deltaFits, fromSnapshot, withAlerts, type BranchState } from './branchState'
import type { Envelope } from './types'

// The live connection.
//
// A screen in a branch runs for months without anyone touching it. The network will
// drop, the server will be restarted for a deploy, a display's power will blink. So
// this reconnects on its own, forever, and every reconnection begins with a fresh
// snapshot — a screen is never left showing a queue from before it lost the link.

export type ConnectionStatus = 'connecting' | 'live' | 'reconnecting'

export interface LiveHandlers {
  onState: (state: BranchState) => void
  onStatus: (status: ConnectionStatus) => void
  onAnnounce?: (announce: Announce) => void
  /** The machine this screen claims to be is already running somewhere else. The
   *  page shows that instead of the queue, and offers to take it over. */
  onInUse?: (heldBy: string) => void
  /** A feedback tablet's press token, handed to this connection alone when it
   *  claimed its machine; '' when the connection that carried it has gone. A press
   *  is believed only with the token of the socket that is open now, so a new one
   *  always replaces the old. */
  onPressToken?: (token: string) => void
  /** A campaign or playlist changed what this branch's screens play: ask for the design again. */
  onLayout?: () => void
}

export interface LiveOptions {
  /** Claim the machine even if another connection holds it, disconnecting them.
   *  Only ever set by somebody pressing a button that says so. */
  takeOver?: boolean
  /** The address of an unattended screen — /display/BAKU-01/TV1 — when this
   *  connection is one.
   *
   *  A screen connects to its own endpoint rather than the operators' one. The
   *  branch and the machine are settled by the address before the socket opens,
   *  so there is nothing to subscribe to and nothing to claim: the server has
   *  already done both, and what comes back is the redacted view a screen anybody
   *  can open is allowed to have. */
  screen?: { branchCode: string; unitCode: string }
}

type Announce = NonNullable<Envelope['delta']>['announce']

/** How many calls lost across a gap are kept for the snapshot that answers it. */
const MAX_PENDING_ANNOUNCES = 3

/** How long a link may say nothing before it is taken to be dead. The server
 *  sends a heartbeat every thirty seconds, so this is two of them missed and a
 *  margin. A pulled cable closes nothing — the browser's socket goes on reading as
 *  open — and the server's pings never reach the page, so without this a quiet
 *  kiosk would never learn it had been cut off. */
export const SILENCE_MS = 75_000

export class LiveConnection {
  private socket: WebSocket | null = null
  private state: BranchState | null = null
  private closed = false
  private attempt = 0
  private reconnectTimer: number | undefined
  private silenceTimer: number | undefined
  /** Calls carried by deltas that could not be applied, each with the version it
   *  made. The snapshot asked for in their place holds the ticket as called but
   *  says nothing about a call, so without these a call made across a gap would
   *  never be announced. Told once the snapshot that reaches their version comes. */
  private pendingAnnounces: { version: number; announce: Announce }[] = []
  /** The last "showing" report, sent again on every new socket: the server holds
   *  it on the connection, so a reconnection that did not repeat it would leave the
   *  machine reported as showing nothing. */
  private showing: string | null = null
  /** The last printer report, sent again on every new socket like the showing one. */
  private printer: string | null = null

  constructor(
    private readonly branchId: string,
    private readonly label: string,
    private readonly handlers: LiveHandlers,
    private options: LiveOptions = {},
  ) {}

  /** Claim the machine from whoever is holding it, and reconnect.
   *
   *  The take-over is deliberately not remembered: it applies to this attempt, so
   *  a screen that takes a unit over and then loses its network does not spend the
   *  rest of the day stealing the unit back from a machine that has legitimately
   *  replaced it. */
  takeOver(): void {
    this.options = { ...this.options, takeOver: true }
    // The in-use refusal marked this connection closed; taking over opens it
    // again, so a later drop (a redefinition, a tablet's reconnect) is followed
    // by a reconnection as any other drop is.
    this.closed = false
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer)
    // The old socket is let go before it closes, so its close neither schedules a
    // second reconnection beside the one opened here nor touches the new token.
    const old = this.socket
    this.socket = null
    old?.close()
    this.handlers.onPressToken?.('')
    this.attempt = 0
    this.open()
  }

  /** Ask for the whole branch again, now.
   *
   *  For the moments the server says something will change and publishes nothing
   *  when it does: a customer coming out of a recycle cooldown or a clerk's delay,
   *  which changes who a desk calls next (CounterView.nextCheckAt). A delta cannot
   *  carry that — there is no command behind it, so no new version, and a delta
   *  that is not newer is skipped. The snapshot that answers replaces the state
   *  whatever its version. Does nothing while the link is down; the reconnection
   *  begins with a snapshot anyway. */
  resync(): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return
    this.socket.send(JSON.stringify({ type: 'resync', branchId: this.branchId }))
  }

  /** Tell the server what this machine has drawn: which design, at which version,
   *  with which build of the app. What the editor's "3 of 3 showing it" and the
   *  Machines page are made from. */
  report(showing: { layoutId?: string; version: number; build?: string }): void {
    this.showing = JSON.stringify({ type: 'showing', layoutId: showing.layoutId ?? '', version: showing.version, build: showing.build ?? '' })
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(this.showing)
  }

  /** Tell the server what this machine's printer is doing, for the Machines page.
   *  Only a change is sent. */
  reportPrinter(state: 'ready' | 'faulty' | 'absent', error = ''): void {
    const next = JSON.stringify({ type: 'printer', printer: state, printerError: error.slice(0, 200) })
    if (next === this.printer) return
    this.printer = next
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(next)
  }

  /** Drop the link and make it again, as if the network had blinked.
   *
   *  For a feedback tablet whose press was not believed to come from it: its
   *  address or its token no longer matches what the server holds, and the only
   *  way to a token the server believes is a new connection. */
  reconnect(): void {
    this.socket?.close()
  }

  start(): void {
    this.closed = false
    this.open()
  }

  stop(): void {
    this.closed = true
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer)
    this.unwatch()
    this.socket?.close()
    this.socket = null
  }

  private open(): void {
    this.handlers.onStatus(this.attempt === 0 ? 'connecting' : 'reconnecting')

    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const screen = this.options.screen
    const url = screen
      ? `${scheme}://${window.location.host}/api/v1/screen/${encodeURIComponent(
          screen.branchCode,
        )}/${encodeURIComponent(screen.unitCode)}/live${this.options.takeOver ? '?takeOver=true' : ''}`
      : `${scheme}://${window.location.host}/api/v1/ws`
    const socket = new WebSocket(url)
    this.socket = socket

    socket.onopen = () => {
      this.attempt = 0
      this.watch(socket)
      // An addressed screen has nothing to ask for. Its branch and its machine
      // were settled by the URL it connected with, which is what makes the
      // endpoint safe to leave open.
      if (!screen) {
        socket.send(
          JSON.stringify({
            type: 'subscribe',
            branchId: this.branchId,
            label: this.label,
          }),
        )
      }
      if (this.showing) socket.send(this.showing)
      if (this.printer) socket.send(this.printer)
      // Spent on the attempt that carried it, so a reconnection does not quietly
      // take the machine over again.
      this.options = { ...this.options, takeOver: false }
    }

    // A socket that has been replaced speaks for nothing any more.
    socket.onmessage = (event) => {
      if (this.socket !== socket) return
      this.watch(socket)
      this.receive(event.data as string)
    }

    socket.onclose = () => {
      // Only the current socket's close counts: a take-over opens the next one
      // before the old one has finished closing, and the old one must neither
      // wipe the new token nor schedule a second reconnection.
      if (this.socket !== socket) return
      this.lost()
    }

    // An error is always followed by a close, so the reconnection is handled in one
    // place rather than in two that could both fire.
    socket.onerror = () => socket.close()
  }

  /** The current socket is gone, by its own close or by its silence. */
  private lost(): void {
    this.unwatch()
    // The token belonged to this socket, and is worth nothing once it is gone.
    this.handlers.onPressToken?.('')
    if (this.closed) return
    this.handlers.onStatus('reconnecting')
    this.scheduleReconnect()
  }

  /** Starts, or starts again, the wait for the next word from this socket. */
  private watch(socket: WebSocket): void {
    this.unwatch()
    this.silenceTimer = window.setTimeout(() => {
      if (this.socket !== socket) return
      // Let go before closing: a dead link's close can take minutes to arrive,
      // and when it does it must find a socket that no longer speaks for anything
      // rather than schedule a second reconnection.
      this.socket = null
      socket.close()
      this.lost()
    }, SILENCE_MS)
  }

  private unwatch(): void {
    if (this.silenceTimer) window.clearTimeout(this.silenceTimer)
    this.silenceTimer = undefined
  }

  private receive(raw: string): void {
    let envelope: Envelope
    try {
      envelope = JSON.parse(raw) as Envelope
    } catch {
      return
    }

    switch (envelope.type) {
      case 'snapshot': {
        if (!envelope.snapshot) return
        this.state = fromSnapshot(envelope.snapshot)
        this.handlers.onStatus('live')
        this.handlers.onState(this.state)
        // A snapshot never announces on its own: only a call kept from a delta
        // that could not be applied, once the snapshot is new enough to hold it.
        // The call page's ticketId:at dedupe and the voice's version check keep a
        // call that also arrives some other way from being told twice.
        this.flushAnnounces(this.state.version)
        return
      }

      case 'delta': {
        const delta = envelope.delta
        if (!delta || !this.state) return

        switch (deltaFits(this.state, delta)) {
          case 'skip':
            return
          case 'resync':
            // A message was missed. Rather than show a queue that is subtly wrong,
            // the whole state is asked for again. A call on this delta is kept for
            // the snapshot that answers.
            if (delta.announce && !this.pendingAnnounces.some((p) => p.version === delta.version)) {
              this.pendingAnnounces.push({ version: delta.version, announce: delta.announce })
              // Only the latest few: calls older than that would be late news on the
              // wall, and a server that never answers must not grow this without end.
              if (this.pendingAnnounces.length > MAX_PENDING_ANNOUNCES) this.pendingAnnounces.shift()
            }
            this.socket?.send(JSON.stringify({ type: 'resync', branchId: this.branchId }))
            return
          case 'apply':
            this.state = applyDelta(this.state, delta)
            this.handlers.onState(this.state)
            if (delta.announce) this.handlers.onAnnounce?.(delta.announce)
            // A kept call this state has passed was not answered by a snapshot:
            // the same delta arrived again and applied, and has been told above.
            this.pendingAnnounces = this.pendingAnnounces.filter((p) => p.version > this.state!.version)
            return
        }
        return
      }

      case 'press':
        this.handlers.onPressToken?.(envelope.pressToken ?? '')
        return

      case 'layout':
        this.handlers.onLayout?.()
        return

      case 'alerts': {
        // No version: no command made it, so it cannot be out of order with the
        // deltas, and the whole list is sent each time. Before the snapshot there
        // is nothing to put it on, and the snapshot carries the same list.
        if (!this.state) return
        this.state = withAlerts(this.state, envelope.alerts ?? [])
        this.handlers.onState(this.state)
        return
      }

      case 'error':
        if (envelope.code === 'in_use') {
          // Not a fault to retry. Another machine is this screen, and reconnecting
          // in a loop would be a fight between two of them.
          this.closed = true
          this.unwatch()
          this.socket?.close()
          this.handlers.onInUse?.(envelope.error ?? '')
          return
        }
        // Reported through the status rather than thrown: a screen on a wall has
        // nobody to catch an exception.
        console.warn('live channel:', envelope.error)
        return

      default:
        return
    }
  }

  private flushAnnounces(version: number): void {
    const due = this.pendingAnnounces.filter((p) => p.version <= version)
    if (due.length === 0) return
    this.pendingAnnounces = this.pendingAnnounces.filter((p) => p.version > version)
    for (const p of due) this.handlers.onAnnounce?.(p.announce)
  }

  /**
   * Back off, but never give up and never wait long.
   *
   * A display that has been dark for an hour because it backed off to ten minutes
   * is useless. The ceiling is twelve seconds: long enough not to hammer a server
   * that is restarting, short enough that a branch is never waiting on it.
   */
  private scheduleReconnect(): void {
    this.attempt++
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer)
    this.reconnectTimer = window.setTimeout(() => this.open(), reconnectDelay(this.attempt, Math.random()))
  }
}

/** The wait before reconnection attempt n (from 1), for random in [0, 1): half to one
 *  and a half times a doubling base capped at 8 s, so a branch's screens dropped
 *  together do not all come back in the same moment. */
export function reconnectDelay(attempt: number, random: number): number {
  const base = Math.min(8_000, 1_000 * 2 ** Math.max(0, attempt - 1))
  return base * (0.5 + random)
}
